import { useMemo, useState } from 'react';
import { Check, Minus, Plus } from 'lucide-react';
import { defaultSelections, priceItem, SelectionError, selectionMessage, type Selections } from '@pizza-roma/shared';
import { useCart } from '../../lib/cart';
import { money } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { Modal } from '../../components/ui';
import { useI18n } from '../../i18n';
import type { MenuItem } from './context';

export function ProductModal({ item, onClose }: { item: MenuItem; onClose: () => void }) {
  const [sel, setSel] = useState<Selections>(() => defaultSelections(item.config));
  const [qty, setQty] = useState(1);
  const cart = useCart();
  const toast = useToast();
  const { t, lang, demo } = useI18n();

  const priced = useMemo(() => {
    try {
      return { ...priceItem(item.basePriceCents, item.config, sel), error: null as string | null };
    } catch (e) {
      const error = e instanceof SelectionError ? selectionMessage(e.issue, lang, demo) : (e as Error).message;
      return { unitPriceCents: item.basePriceCents, options: [], error };
    }
  }, [item, sel, lang, demo]);

  const toggle = (groupId: string, optionId: string, single: boolean, max?: number) =>
    setSel((s) => {
      const cur = s[groupId] ?? [];
      if (single) {
        const g = item.config.groups.find((x) => x.id === groupId);
        return { ...s, [groupId]: cur.includes(optionId) && !g?.required ? [] : [optionId] };
      }
      if (cur.includes(optionId)) return { ...s, [groupId]: cur.filter((x) => x !== optionId) };
      if (max && cur.length >= max) return s;
      return { ...s, [groupId]: [...cur, optionId] };
    });

  const add = () => {
    if (priced.error) return;
    cart.add({
      menuItemId: item.id,
      name: item.name,
      imageUrl: item.imageUrl,
      quantity: qty,
      selections: sel,
      optionsLabel: priced.options.map((o) => o.optionName).join(', '),
      optionNames: priced.options.map((o) => o.optionName),
      unitPriceCents: priced.unitPriceCents,
    });
    toast(t.product.added(demo(item.name)), 'success');
    onClose();
  };

  return (
    <Modal
      title={demo(item.name)}
      onClose={onClose}
      testId="product-modal"
      footer={
        <div className="row-between" style={{ width: '100%' }}>
          <div className="qty">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label={t.product.less}>
              <Minus size={16} />
            </button>
            <span data-testid="qty">{qty}</span>
            <button onClick={() => setQty((q) => Math.min(50, q + 1))} aria-label={t.product.more}>
              <Plus size={16} />
            </button>
          </div>
          <button className="btn btn-primary btn-lg" onClick={add} disabled={!!priced.error} data-testid="add-to-cart">
            {t.product.addFor}
            <span data-testid="product-price">{money(priced.unitPriceCents * qty)}</span>
          </button>
        </div>
      }
    >
      <div className="stack-lg">
        <div className="pd-img" style={{ margin: '-20px -20px 0', borderRadius: 0 }}>
          <img src={item.imageUrl ?? ''} alt={demo(item.name)} />
        </div>
        <div className="row-between" style={{ alignItems: 'start' }}>
          <p className="muted">{demo(item.description)}</p>
          <b className="price" style={{ fontSize: 22 }}>
            {money(priced.unitPriceCents)}
          </b>
        </div>
        {item.config.groups.map((g) => (
          <div key={g.id} className="opt-group">
            <h4>
              {demo(g.name)}
              {g.required ? (
                <span className="badge no-dot b-new">{t.common.required}</span>
              ) : (
                <span className="muted small">{g.maxSelections ? t.product.max(g.maxSelections) : t.product.optional}</span>
              )}
            </h4>
            <div className="opt-grid">
              {g.options.map((o) => {
                const on = (sel[g.id] ?? []).includes(o.id);
                return (
                  <button
                    key={o.id}
                    className={`opt ${on ? 'selected' : ''}`}
                    onClick={() => toggle(g.id, o.id, g.type === 'single', g.maxSelections)}
                    aria-pressed={on}
                    data-testid={`opt-${g.id}-${o.id}`}
                  >
                    <span className="row-between">
                      <b>{demo(o.name)}</b>
                      {on && <Check size={16} color="var(--red)" />}
                    </span>
                    <small>{o.priceDeltaCents ? `+ ${money(o.priceDeltaCents)}` : t.product.included}</small>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {priced.error && <p className="error-text">{priced.error}</p>}
      </div>
    </Modal>
  );
}
